import React, { useState, useEffect } from 'react';
import { Container, Button, Card, CardBody, Spinner, Row, Col, Form, FormGroup, Label, Input, Table, Modal, ModalHeader, ModalBody } from 'reactstrap';
import axios from 'axios';
import InhireVagaKanban from '../../components/Modals/ModalPedidoCompra/InhireVagaKanban';

const TestPage = () => {
    const API_BASE = process.env.NODE_ENV === 'production'
        ? 'https://oikos.ecoss.is/oikos/_inhire_api.php?action='
        : '/api/integration/';

    // ==========================================
    // ESTADOS: CRIACAO DE VAGAS
    // ==========================================
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        status: 'open',
        contractType: 'CLT', // Valor inicial padrão
        locationCity: '',
        locationState: '',
        locationCountry: 'BR',
        postingStatus: 'published',
        activeJobBoards: []
    });
    const [logs, setLogs] = useState([]);
    const [loadingCreate, setLoadingCreate] = useState(false);

    // ==========================================
    // ESTADOS: LISTAGEM (PAGINAÇÃO POR CURSOR)
    // ==========================================
    const [jobList, setJobList] = useState([]);
    const [loadingList, setLoadingList] = useState(false);

    // Controles do Cursor
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [pageHistory, setPageHistory] = useState([null]); // Guarda o ID de inicio de cada página

    // ==========================================
    // ESTADOS: CANDIDATOS
    // ==========================================
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedJobId, setSelectedJobId] = useState(null);

    const toggleModal = () => setModalOpen(!modalOpen);

    // ==========================================
    // FUNCOES AUXILIARES
    // ==========================================
    const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
    const handleCheckboxChange = (e) => {
        const { value, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            activeJobBoards: checked ? [...prev.activeJobBoards, value] : prev.activeJobBoards.filter(b => b !== value)
        }));
    };
    const addLog = (message) => setLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);

    // ==========================================
    // LÓGICA: CRIAR VAGA
    // ==========================================
    const handleCreateVacancy = async () => {
        if (!formData.name || !formData.locationCity || !formData.locationState) {
            alert("Por favor, preencha o Nome da Vaga, Cidade e Estado."); return;
        }
        setLoadingCreate(true); setLogs([]); addLog("Iniciando...");

        try {
            const jobData = {
                name: formData.name,
                description: formData.description,
                status: formData.status,
                contractType: [formData.contractType], // O InHire espera um array aqui
                locationCity: formData.locationCity,
                locationState: formData.locationState,
                locationCountry: formData.locationCountry,
                locationRequired: false,
                talentSuggestions: false,
                postingPage: {
                    status: formData.postingStatus,
                    displayName: formData.name,
                    activeJobBoards: formData.activeJobBoards
                }
            };

            const createResponse = await axios.post(`${API_BASE}create-job`, jobData);
            addLog("SUCESSO: Vaga criada!");

            // Reseta a paginação e busca do início
            setPageHistory([null]);
            handleFetchJobs(1, null);

        } catch (error) {
            addLog(`ERRO: ${error.message}`);
            if (error.response && error.response.data) {
                addLog(`Detalhes inHire: ${JSON.stringify(error.response.data)}`);
            }
            console.error(error);
        } finally {
            setLoadingCreate(false);
        }
    };

    // ==========================================
    // LÓGICA: LISTAR VAGAS (LEAN)
    // ==========================================
    const handleFetchJobs = async (pageNumber, startId = null) => {
        setLoadingList(true);
        try {
            // Chamando a rota de listagem
            const response = await axios.post(`${API_BASE}list-jobs`, {
                limit: 5,
                lastJobId: startId
            });

            const fetchedData = response.data;
            let fetchedJobs = [];

            if (Array.isArray(fetchedData)) {
                fetchedJobs = fetchedData;
            } else if (fetchedData && Array.isArray(fetchedData.items)) {
                fetchedJobs = fetchedData.items;
            } else if (fetchedData && Array.isArray(fetchedData.data)) {
                fetchedJobs = fetchedData.data;
            }

            // Ordenando da mais recente para a mais antiga (createdAt descendente)
            fetchedJobs.sort((a, b) => {
                const dateA = new Date(a.createdAt || 0);
                const dateB = new Date(b.createdAt || 0);
                return dateB - dateA; // Maior (mais recente) primeiro
            });

            setJobList(fetchedJobs);

            // Verifica se tem mais páginas para habilitar/desabilitar o botão "Próxima"
            setHasMore(response.data.hasMore !== undefined ? response.data.hasMore : fetchedJobs.length === 5);
            setCurrentPage(pageNumber);

        } catch (error) {
            console.error("Erro ao buscar vagas:", error);
            console.warn("Falha ao carregar a lista de vagas. Verifique o servidor.");
        } finally {
            setLoadingList(false);
        }
    };

    // Botão PRÓXIMA Página
    const handleNextPage = () => {
        if (jobList.length === 0) return;
        const lastId = jobList[jobList.length - 1].id;

        const newHistory = [...pageHistory];
        newHistory[currentPage] = lastId;
        setPageHistory(newHistory);

        handleFetchJobs(currentPage + 1, lastId);
    };

    // Botão PÁGINA ANTERIOR
    const handlePrevPage = () => {
        const prevStartId = pageHistory[currentPage - 2];
        handleFetchJobs(currentPage - 1, prevStartId);
    };

    // Executa a busca inicial ao abrir a tela
    useEffect(() => {
        handleFetchJobs(1, null);
    }, []);

    // ==========================================
    // LÓGICA: BUSCAR CANDIDATOS DA VAGA
    // ==========================================
    const handleViewCandidates = (jobId) => {
        setSelectedJobId(jobId);
        setModalOpen(true);
    };

    // ==========================================
    // RENDERIZACAO
    // ==========================================
    return (
        <div className="page-content">
            <Container fluid>
                <h1>Painel de Integracao - OIKOS & inHire</h1>
                <hr />

                {/* SESSÃO 1: CRIAÇÃO */}
                <Row className="mb-5">
                    <Col md={7}>
                        <Card className="h-100">
                            <CardBody>
                                <h4 className="mb-4">Criar Vaga</h4>
                                <Form>
                                    <FormGroup>
                                        <Label>Nome *</Label>
                                        <Input type="text" name="name" value={formData.name} onChange={handleInputChange} />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Descricao</Label>
                                        <Input type="textarea" name="description" rows="3" value={formData.description} onChange={handleInputChange} />
                                    </FormGroup>

                                    <Row>
                                        <Col md={4}>
                                            <FormGroup>
                                                <Label>Contrato</Label>
                                                <Input type="select" name="contractType" value={formData.contractType} onChange={handleInputChange}>
                                                    <option value="CLT">CLT</option>
                                                    <option value="PJ">PJ</option>
                                                    <option value="Estágio">Estágio</option>
                                                    <option value="Contrato Temporário">Contrato Temporário</option>
                                                    <option value="Associado">Associado</option>
                                                    <option value="Cooperado">Cooperado</option>
                                                    <option value="Menor aprendiz">Menor aprendiz</option>
                                                    <option value="Autônomo">Autônomo</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md={4}>
                                            <FormGroup>
                                                <Label>Status Interno</Label>
                                                <Input type="select" name="status" value={formData.status} onChange={handleInputChange}>
                                                    <option value="open">Ativa</option>
                                                    <option value="closed">Fechada</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md={4}>
                                            <FormGroup>
                                                <Label>Visibilidade Externa</Label>
                                                <Input type="select" name="postingStatus" value={formData.postingStatus} onChange={handleInputChange}>
                                                    <option value="published">Publica</option>
                                                    <option value="unpublished">Privada</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Cidade *</Label>
                                                <Input type="text" name="locationCity" value={formData.locationCity} onChange={handleInputChange} />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Estado (UF) *</Label>
                                                <Input type="text" name="locationState" maxLength="2" value={formData.locationState} onChange={handleInputChange} />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Button color="primary" className="w-100 mt-3" onClick={handleCreateVacancy} disabled={loadingCreate}>
                                        {loadingCreate ? <Spinner size="sm" /> : "Criar e Configurar Vaga"}
                                    </Button>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col md={5}>
                        <Card className="bg-dark text-white border-0 h-100">
                            <CardBody style={{ height: '480px', overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                {logs.length === 0 ? "Aguardando..." : logs.map((log, i) => <div key={i}>{log}</div>)}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* SESSÃO 2: LISTAGEM PAGINADA */}
                <Row>
                    <Col md={12}>
                        <Card>
                            <CardBody>
                                <div className="d-flex justify-content-between mb-4">
                                    <h4>Vagas Cadastradas</h4>
                                    <Button size="sm" onClick={() => handleFetchJobs(1, null)} disabled={loadingList}>
                                        {loadingList ? <Spinner size="sm" /> : "Recarregar Início"}
                                    </Button>
                                </div>

                                {loadingList ? (
                                    <div className="text-center p-5"><Spinner color="primary" /></div>
                                ) : (
                                    <>
                                        <Table responsive hover className="align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>ID Interno</th>
                                                    <th>Nome da Vaga</th>
                                                    <th>Localidade</th>
                                                    <th>Contrato</th>
                                                    <th>Candidatos</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {jobList.length === 0 ? (
                                                    <tr><td colSpan="5" className="text-center">Nenhuma vaga encontrada.</td></tr>
                                                ) : (
                                                    jobList.map((job, index) => (
                                                        <tr key={job.id || index}>
                                                            <td className="text-muted">{job.id ? job.id.substring(0, 8) + '...' : 'N/A'}</td>
                                                            <td><strong>{job.jobTitle || job.name || 'Sem Titulo'}</strong></td>
                                                            <td>{job.locationCity} / {job.locationState}</td>
                                                            <td>{Array.isArray(job.contractType) ? job.contractType.join(', ') : job.contractType}</td>
                                                            <td>
                                                                <Button
                                                                    color="info"
                                                                    size="sm"
                                                                    outline
                                                                    onClick={() => handleViewCandidates(job.id)}
                                                                >
                                                                    👤 {job.talentsCount || 0}
                                                                </Button>
                                                            </td>
                                                            <td>
                                                                <span className="badge bg-secondary">
                                                                    {(job.jobPage?.status || job.status || 'N/A').toUpperCase()}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </Table>

                                        {/* CONTROLES CURSOR */}
                                        <div className="d-flex justify-content-between mt-4">
                                            <Button outline color="primary" disabled={currentPage <= 1} onClick={handlePrevPage}>
                                                &laquo; Anterior
                                            </Button>
                                            <span className="text-muted">Página {currentPage}</span>
                                            <Button outline color="primary" disabled={!hasMore || jobList.length === 0} onClick={handleNextPage}>
                                                Próxima &raquo;
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* MODAL DE CANDIDATOS */}
                <Modal isOpen={modalOpen} toggle={toggleModal} size="xl" centered>
                    <ModalHeader toggle={toggleModal}>Candidatos da Vaga — Etapas inHire</ModalHeader>
                    <ModalBody style={{ minHeight: '350px' }}>
                        <InhireVagaKanban jobId={selectedJobId} active={modalOpen} />
                    </ModalBody>
                </Modal>

            </Container>
        </div>
    );
};

export default TestPage;